import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (24h cooldown)
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 86400000) return;

    // The `beforeinstallprompt` event may have already fired (and been stashed
    // on window by index.html) before this component mounted. Pick it up.
    if (window.__deferredInstallPrompt) {
      setDeferredPrompt(window.__deferredInstallPrompt);
      setShowBanner(true);
    }

    const handler = (e) => {
      e.preventDefault();
      window.__deferredInstallPrompt = e;
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    // Fired when index.html captured the native event before React mounted.
    const customHandler = () => {
      if (window.__deferredInstallPrompt) {
        setDeferredPrompt(window.__deferredInstallPrompt);
        setShowBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('pwa-installable', customHandler);

    // iOS detection — show manual instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandalone = window.navigator.standalone;
    if (isIOS && !isInStandalone) {
      setShowBanner(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('pwa-installable', customHandler);
    };
  }, []);

  const handleInstall = async () => {
    const promptEvent = deferredPrompt || window.__deferredInstallPrompt;
    if (!promptEvent) return;
    promptEvent.prompt();
    const result = await promptEvent.userChoice;
    if (result.outcome === 'accepted') {
      setIsInstalled(true);
    }
    window.__deferredInstallPrompt = null;
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  if (isInstalled || !showBanner) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[90] p-3 animate-in slide-in-from-bottom-4" data-testid="pwa-install-banner">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-sky-100 p-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-gray-900">Install Vynce Portal</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {isIOS
                    ? 'Tap the Share button, then "Add to Home Screen"'
                    : 'Add to your home screen for quick access'}
                </p>
              </div>
              <button onClick={handleDismiss} className="p-1 rounded-full hover:bg-gray-100 flex-shrink-0" data-testid="pwa-dismiss-btn">
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            {!isIOS && deferredPrompt && (
              <Button
                size="sm"
                className="mt-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg h-9 px-4 text-xs font-semibold"
                onClick={handleInstall}
                data-testid="pwa-install-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Install App
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
